import { apiClient } from './api'

// ========================================
// Types & Interfaces
// ========================================

export type BlockStorageVolume = {
  id: string
  name: string
  description?: string
  size_gb: number
  storage_type: string
  status:
    | 'creating'
    | 'available'
    | 'attaching'
    | 'attached'
    | 'detaching'
    | 'resizing'
    | 'deleting'
    | 'error'
    | 'deleted'
  status_message?: string
  attached_vm?: {
    id: string
    name: string
    status: string
  }
  attached_scsi_slot?: string
  unit_price_per_gb: number
  monthly_cost: number
  currency: string
  billing_cycle: string
  billing_status: string
  cluster_id?: string
  product_id?: string
  created_at: string
  updated_at?: string
}

export type VolumeListResponse = {
  volumes: BlockStorageVolume[]
  total: number
  skip: number
  limit: number
  has_more: boolean
}

export type CreateVolumeRequest = {
  product_id: string
  name: string
  size_gb: number
  payment_option: 'credit_card' | 'credit_account'
  payment_method_id?: string
  reseller_id?: string
}

export type AttachVolumeRequest = {
  vm_instance_id: string
}

export type ResizeVolumeRequest = {
  new_size_gb: number
}

export type BlockStorageProduct = {
  id: string
  name: string
  description?: string
  storage_type: string
  price_per_storage_gb: number
  base_price: number
  setup_fee: number
  currency: string
  billing_cycle: string
  min_storage: number
  max_storage: number
  specifications: Record<string, unknown>
  original_price_per_storage_gb: number
  markup_applied: boolean
  markup_percentage?: number
}

// ========================================
// API Functions
// ========================================

const blockStorageApi = {
  /**
   * List block storage products available for purchase
   */
  listProducts: async (resellerId?: string): Promise<BlockStorageProduct[]> => {
    const params = new URLSearchParams()
    if (resellerId) params.append('reseller_id', resellerId)
    return apiClient.get<BlockStorageProduct[]>(
      `/api/v1/end-customer/block-storage/products?${params.toString()}`,
    )
  },

  /**
   * List volumes for the current customer
   */
  listVolumes: async (params?: {
    status?: string
    search?: string
    /** Só os volumes anexados a esta VM (issue #759) — filtro do backend, não do navegador. */
    vm_instance_id?: string
    skip?: number
    limit?: number
  }): Promise<VolumeListResponse> => {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.append('status', params.status)
    if (params?.search) searchParams.append('search', params.search)
    if (params?.vm_instance_id) searchParams.append('vm_instance_id', params.vm_instance_id)
    if (params?.skip !== undefined) searchParams.append('skip', String(params.skip))
    if (params?.limit !== undefined) searchParams.append('limit', String(params.limit))
    return apiClient.get<VolumeListResponse>(
      `/api/v1/end-customer/block-storage/volumes?${searchParams.toString()}`,
    )
  },

  /**
   * Get a specific volume
   */
  getVolume: async (volumeId: string): Promise<BlockStorageVolume> => {
    return apiClient.get<BlockStorageVolume>(
      `/api/v1/end-customer/block-storage/volumes/${volumeId}`,
    )
  },

  /**
   * Create a new block storage volume
   */
  createVolume: async (data: CreateVolumeRequest): Promise<BlockStorageVolume> => {
    return apiClient.post<BlockStorageVolume>('/api/v1/end-customer/block-storage/volumes', data)
  },

  /**
   * Delete a volume (must be in 'available' status)
   */
  deleteVolume: async (volumeId: string): Promise<BlockStorageVolume> => {
    return apiClient.delete<BlockStorageVolume>(
      `/api/v1/end-customer/block-storage/volumes/${volumeId}`,
    )
  },

  /**
   * Attach a volume to a VM
   */
  attachVolume: async (
    volumeId: string,
    data: AttachVolumeRequest,
  ): Promise<BlockStorageVolume> => {
    return apiClient.post<BlockStorageVolume>(
      `/api/v1/end-customer/block-storage/volumes/${volumeId}/attach`,
      data,
    )
  },

  /**
   * Detach a volume from its VM
   */
  detachVolume: async (volumeId: string): Promise<BlockStorageVolume> => {
    return apiClient.post<BlockStorageVolume>(
      `/api/v1/end-customer/block-storage/volumes/${volumeId}/detach`,
    )
  },

  /**
   * Resize a volume (grow only)
   */
  resizeVolume: async (
    volumeId: string,
    data: ResizeVolumeRequest,
  ): Promise<BlockStorageVolume> => {
    return apiClient.post<BlockStorageVolume>(
      `/api/v1/end-customer/block-storage/volumes/${volumeId}/resize`,
      data,
    )
  },
}

// ========================================
// Helper functions
// ========================================

export const getVolumeStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    creating: 'bg-blue-100 text-blue-800',
    available: 'bg-green-100 text-green-800',
    attaching: 'bg-yellow-100 text-yellow-800',
    attached: 'bg-purple-100 text-purple-800',
    detaching: 'bg-yellow-100 text-yellow-800',
    resizing: 'bg-blue-100 text-blue-800',
    deleting: 'bg-red-100 text-red-800',
    error: 'bg-red-100 text-red-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

export const getVolumeStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    creating: 'Criando',
    available: 'Disponível',
    attaching: 'Anexando',
    attached: 'Anexado',
    detaching: 'Desanexando',
    resizing: 'Redimensionando',
    deleting: 'Excluindo',
    error: 'Erro',
    deleted: 'Excluído',
  }
  return labels[status] || status
}

export const formatStorageSize = (gb: number): string => {
  if (gb >= 1000) return `${(gb / 1000).toFixed(1)} TB`
  return `${gb} GB`
}

export const formatMonthlyPrice = (value: number, currency: string = 'BRL'): string => {
  return `${new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(value)}/mês`
}

export const isVolumeTransitioning = (status: string): boolean => {
  return ['creating', 'attaching', 'detaching', 'resizing', 'deleting'].includes(status)
}

export default blockStorageApi
